import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableItem
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useQuickLinks from "../../hooks/useQuickLinks";
import useDeleteQuickLink from "../../hooks/useDeleteQuickLink";
import useToggleQuickLinkPublished from "../../hooks/useToggleQuickLinkPublished";
import useMoveQuickLink from "../../hooks/useMoveQuickLink";

function mapQuickLinksToContentTableItems(
	activeLinks: QuickLink[],
	draftLinks: QuickLink[]
): ContentTableItem[] {
	const live = [...activeLinks]
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
		.map((link, index) => ({
			id: link.id,
			name: link.heading,
			createdBy: link.createdBy
				? `${link.createdBy.firstName} ${link.createdBy.lastName}`
				: undefined,
			lastUpdated: link.updatedAt ?? link.createdAt,
			isActive: true,
			order: index + 1
		}));

	const draft = draftLinks.map((link) => ({
		id: link.id,
		name: link.heading,
		createdBy: link.createdBy
			? `${link.createdBy.firstName} ${link.createdBy.lastName}`
			: undefined,
		lastUpdated: link.updatedAt ?? link.createdAt,
		isActive: false
	}));

	return [...live, ...draft];
}

const QuickLinks = () => {
	const navigate = useNavigate();
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);

	const { data: activeLinks = [] } = useQuickLinks(true);
	const { data: draftLinks = [] } = useQuickLinks(false);

	const { mutate: togglePublished } = useToggleQuickLinkPublished();
	const { mutate: deleteQuickLink, isLoading: isDeleting } =
		useDeleteQuickLink();
	const { mutate: moveQuickLink } = useMoveQuickLink();

	const items = mapQuickLinksToContentTableItems(activeLinks, draftLinks);

	const handleMove = (item: ContentTableItem, direction: -1 | 1) => {
		moveQuickLink({ id: Number(item.id), offset: direction });
	};

	return (
		<Page>
			<ContentTable
				items={items}
				onRowClick={(item) => navigate(`/app/homepage/quick-links/${item.id}`)}
				onMakeLive={(item) =>
					togglePublished({ id: Number(item.id), published: true })
				}
				onMakeDraft={(item) =>
					togglePublished({ id: Number(item.id), published: false })
				}
				onDelete={(item) => setItemToDelete(item)}
				onMoveUp={(item) => handleMove(item, -1)}
				onMoveDown={(item) => handleMove(item, 1)}
				createPath="/app/homepage/quick-links/create"
				createLabel="Create Quick Link"
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete quick link?"
				description="This action cannot be undone."
				onCancel={() => setItemToDelete(null)}
				onSubmit={() => {
					if (itemToDelete) {
						deleteQuickLink(Number(itemToDelete.id));
						setItemToDelete(null);
					}
				}}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default QuickLinks;
